import { ComponentClass, ElementType, forwardRef, ForwardRefExoticComponent, FunctionComponent } from 'react'

import TypeUtil from '@/utils/typeUtil'

type DataTestIdFunction = (predefinedId: string) => string
type DataTestIdValue = string | DataTestIdFunction | undefined

type ComponentWithTestId<T> = T extends ForwardRefExoticComponent<infer P>
  ? ForwardRefExoticComponent<PropsWithTestId<P>>
  : T extends ComponentClass<infer P>
  ? ComponentClass<PropsWithTestId<P>>
  : T extends FunctionComponent<infer P>
  ? FunctionComponent<PropsWithTestId<P>>
  : never

export type PropsWithTestId<P> = P & {
  'data-testid'?: DataTestIdValue
}

/**
 * Enable Component to receive extra 'data-testid' prop.
 *
 * @param Component Component to modify
 * @param id predefined data-testid. this id get passed into 'data-testid' function 'predefinedId' argument
 * @returns Component with type PropsWithTestId {@link PropsWithTestId}
 */
const withTestId = <T extends ElementType>(Component: T, id: string): ComponentWithTestId<T> => {
  const WithTestId: ComponentWithTestId<T> = forwardRef(({ 'data-testid': suppliedDataTestId, ...props }, ref) => {
    const mergedProps = {
      'data-testid': !TypeUtil.isDefined(suppliedDataTestId)
        ? id
        : TypeUtil.isFunction(suppliedDataTestId)
        ? suppliedDataTestId(id)
        : suppliedDataTestId,
      ...props,
    }

    return (
      <Component
        ref={ref}
        {...mergedProps}
      />
    )
  }) as ComponentWithTestId<T>

  return WithTestId
}

export default withTestId
